import { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Spinner } from "@jobber/components/Spinner";
import { Button } from "@jobber/components/Button";
import { InputText } from "@jobber/components/InputText";
import { Glimmer } from "@jobber/components/Glimmer";
import { Heading } from "@jobber/components/Heading";
import { Checkbox } from '@jobber/components/Checkbox';
import { showToast } from "@jobber/components/Toast";

import { useFilter } from "../FilterQuery/FilterQuery";
import { setUserTags, setCurrentEditingTag } from "../../../store/tagsSlice";

import ConfirmModal from "./ConfirmModal";
import Category from "./Category";
import Tag from "./Tag";
import {
	useUserTags,
	useAllTags,
	useAddCategory,
	useDeleteCategory,
	useRearrangeCategories,
	useMoveTag,
	useDeleteTagFromCategory,
} from "./useTagCloud";
import { usePermission } from "../../../hooks/use-permission";

const TagCloudCategory = ({ setPageLoad, isPageLoad }) => {
	const dispatch = useDispatch();
	const { updateFilterByListTags } = useFilter();
	const tagSlice = useSelector((store) => store.tag);
	const entry = useSelector((store) => store.filters);

	const userTags = useSelector((store) => store.tag.userTags);
	const allTags = useSelector((store) => store.tag.allTags);

	const { isLoading: isUserTagsLoading } = useUserTags();
	const { isLoading: isAllTagsLoading } = useAllTags();

	const addCategoryMutation = useAddCategory();
	const deleteCategoryMutation = useDeleteCategory();
	const rearrangeCategoriesMutation = useRearrangeCategories();
	const moveTagMutation = useMoveTag();
	const deleteTagMutation = useDeleteTagFromCategory();
	const { allowed: canManageCategories } = usePermission({ clients: ['update'] });

	const [showInputBox, setShowInputBox] = useState(false);
	const [categoryName, setCategoryName] = useState("");
	const [description, setDescription] = useState("");
	const [isExclusive, setIsExclusive] = useState(false);
	const [expandedCategories, setExpandedCategories] = useState({});
	const [searchTerm, setSearchTerm] = useState("");
	const [currentlyEditing, setCurrentlyEditing] = useState(tagSlice?.currentEditingTag || null);
	const [deleteObject, setDeleteObject] = useState({ open: false, name: "", id: null });
	const [sameTagIdList, setSameTagIdList] = useState({ includeTags: [], excludeTags: [] });

	const handleAppliedTagsFilter = useCallback((uTags, aTags, query) => {
		const includeTagIdList = [];
		const excludeTagIdList = [];

		const processTags = (tags) => {
			tags.forEach((t) => {
				if (query?.includeTags?.includes(t.label)) includeTagIdList.push(t._id);
				if (query?.excludeTags?.includes(t.label)) excludeTagIdList.push(t._id);
			});
		};

		uTags?.forEach((cat) => processTags(cat.tags));
		processTags(aTags);

		setSameTagIdList({ includeTags: includeTagIdList, excludeTags: excludeTagIdList });
	}, []);

	// Handle initial page load filter application
	useEffect(() => {
		if (isPageLoad && userTags.length > 0 && allTags.length > 0) {
			const tempFilter = JSON.parse(localStorage.getItem("tempFilter"));
			if (tempFilter?.includeTags || tempFilter?.excludeTags) {
				handleAppliedTagsFilter(userTags, allTags, tempFilter);
			}
			setPageLoad(false);
		}
	}, [isPageLoad, userTags, allTags, setPageLoad, handleAppliedTagsFilter]);

	// Update sameTagIdList when filters change
	useEffect(() => {
		handleAppliedTagsFilter(userTags, allTags, entry?.filterQuery);
	}, [entry?.isSavedFilterApplied, userTags, allTags, entry?.filterQuery, handleAppliedTagsFilter]);

	const sameTagsClickHandler = useCallback((tagText, key) => {
		const tagIdList = [];
		const processTags = (tags) => {
			tags.forEach((t) => {
				if (t.label === tagText) tagIdList.push(t._id);
			});
		};

		userTags.forEach((cat) => processTags(cat.tags));
		processTags(allTags);

		setSameTagIdList((prev) => ({ ...prev, [key]: tagIdList }));
	}, [userTags, allTags]);

	const handleAddCategory = async () => {
		if (!canManageCategories) {
			showToast({ message: "You do not have permission to manage tag categories", variation: "error" });
			return;
		}
		if (!categoryName) {
			showToast({ message: "Please provide category name", variation: "error" });
			return;
		}
		await addCategoryMutation.mutateAsync({ categoryName, description, isExclusive });
		setCategoryName("");
		setDescription("");
		setIsExclusive(false);
		setShowInputBox(false);
	};

	const deleteCategoryHandler = async () => {
		if (!canManageCategories) {
			showToast({ message: "You do not have permission to manage tag categories", variation: "error" });
			return;
		}
		setDeleteObject((prev) => ({ ...prev, open: false }));
		await deleteCategoryMutation.mutateAsync(deleteObject.id);
		setCurrentlyEditing(null);
		dispatch(setCurrentEditingTag(null));
	};

	const moveCategory = useCallback((dragIndex, hoverIndex) => {
		const newCategories = [...userTags];
		const [draggedItem] = newCategories.splice(dragIndex, 1);
		newCategories.splice(hoverIndex, 0, draggedItem);
		dispatch(setUserTags(newCategories));
	}, [userTags, dispatch]);

	const handleRearrangeCategoriesEnd = useCallback(() => {
		if (!canManageCategories) {
			return;
		}
		rearrangeCategoriesMutation.mutate(userTags);
	}, [userTags, rearrangeCategoriesMutation, canManageCategories]);

	const handleToggle = (id) => {
		if (id === "all") {
			const allExpanded = userTags.length > 0 && userTags.every(t => expandedCategories[t._id]);
			const newState = {};
			userTags.forEach(t => {
				newState[t._id] = !allExpanded;
			});
			setExpandedCategories(newState);
		} else {
			setExpandedCategories(prev => ({
				...prev,
				[id]: !prev[id]
			}));
		}
	};

	const exclusiveTagLabels = useMemo(() => {
		const labels = new Set();
		userTags.forEach(category => {
			if (category.isExclusive) {
				category.tags.forEach(tag => labels.add(tag.label));
			}
		});
		return labels;
	}, [userTags]);

	const filteredTags = useMemo(() => {
		return allTags.filter((tag) => {
			if (exclusiveTagLabels.has(tag.label)) return false;
			return tag.label.toLowerCase().includes(searchTerm.toLowerCase());
		});
	}, [allTags, exclusiveTagLabels, searchTerm]);

	const handleMoveTag = (params) => {
		if (!canManageCategories) {
			showToast({ message: "You do not have permission to manage tag categories", variation: "error" });
			return;
		}
		const { userTags, tagId, tag, source, fromCid, toCid } = params;
		if (fromCid === toCid) return;

		// Optimistic Update
		let newCategories = JSON.parse(JSON.stringify(userTags));
		if (source === "All Tags") {
			const toCat = newCategories.find(c => c._id === toCid);
			if (toCat) {
				toCat.tags = [...toCat.tags, tag];
			}
		} else {
			const fromCat = newCategories.find(c => c._id === fromCid);
			const toCat = newCategories.find(c => c._id === toCid);
			if (fromCat && toCat) {
				fromCat.tags = fromCat.tags.filter(t => t._id !== tagId);
				toCat.tags = [...toCat.tags, tag];
			}
		}
		dispatch(setUserTags(newCategories));

		moveTagMutation.mutate(params, {
			onSuccess: () => {
				updateFilterByListTags({ includeTags: [], excludeTags: [] });
				localStorage.removeItem('tagCloudInclude');
				localStorage.removeItem('notesTagCloudInclude');
				localStorage.removeItem('tagCloudExclude');
				localStorage.removeItem("notesTagCloudExclude");
			}
		});
	};

	const handleDeleteTag = (params) => {
		if (!canManageCategories) {
			showToast({ message: "You do not have permission to manage tag categories", variation: "error" });
			return;
		}
		const { tagId, catId, userTags } = params;

		// Optimistic Update
		let newCategories = JSON.parse(JSON.stringify(userTags));
		const cat = newCategories.find(c => c._id === catId);
		if (cat) {
			cat.tags = cat.tags.filter(t => t._id !== tagId);
		}
		dispatch(setUserTags(newCategories));

		deleteTagMutation.mutate(params);
	};

	const isLoading = isUserTagsLoading || isAllTagsLoading;

	return (
		<div>
			<ConfirmModal
				title={`Delete ${deleteObject.name}?`}
				message={"Deleting this will remove all related tags from your account."}
				open={deleteObject.open}
				onConfirm={deleteCategoryHandler}
				onCancel={() => setDeleteObject((prev) => ({ ...prev, open: false }))}
			/>
			<div className="text-success mb-2 pointer" style={{ width: "fit-content" }} onClick={() => handleToggle("all")}>
				Toggle all
			</div>
			{!isLoading ? (
				userTags.map((t, index) => {
					if (currentlyEditing == null || currentlyEditing == t._id) {
						return (
							<Category
								key={t._id}
								index={index}
								isShow={!!expandedCategories[t._id]}
								handleToggle={handleToggle}
								name={t.categoryName}
								description={t.description}
								isExclusive={t.isExclusive}
								tags={t.tags}
								moveTag={handleMoveTag}
								id={t._id}
								setDeleteObject={setDeleteObject}
								canManageCategories={canManageCategories}
								isEditing={currentlyEditing === t._id}
								moveCategory={moveCategory}
								handleRearrangeCategoriesEnd={handleRearrangeCategoriesEnd}
								setEditing={() => {
									if (!canManageCategories) {
										return;
									}
									setCurrentlyEditing(t._id);
									dispatch(setCurrentEditingTag(t._id));
								}}
								cancelEditing={() => {
									setCurrentlyEditing(null);
									dispatch(setCurrentEditingTag(null));
								}}
								deleteTag={handleDeleteTag}
								sameTagsClickHandler={sameTagsClickHandler}
								userTags={userTags}
							/>
						);
					}
					return null;
				})
			) : (
				<>
					<Glimmer shape={"rectangle"} size={"large"} timing={"base"} />
					<div className="mt-3"></div>
					<Glimmer shape={"rectangle"} size={"base"} timing={"base"} />
				</>
			)}

			<div className="mt-4">
				{canManageCategories && showInputBox ? (
					<div className="mt-1">
						<InputText
							placeholder="Category name"
							name="name"
							size="small"
							value={categoryName}
							onChange={setCategoryName}
						/>
						<div className="mt-2">
							<InputText placeholder="Description" size="small" value={description} onChange={setDescription} />
						</div>
						<div className="mt-2">
							<Checkbox
								checked={isExclusive}
								onChange={setIsExclusive}
								label="Exclusive Category"
							/>
						</div>
						<div className="mt-2">
							{addCategoryMutation.isPending ? (
								<Spinner size={"small"} />
							) : (
								<>
									<Button label={"Add"} size="small" onClick={handleAddCategory} />
									<span className="ms-2"></span>
									<Button label={"Close"} size="small" type="secondary" onClick={() => setShowInputBox(false)} />
								</>
							)}
						</div>
					</div>
				) : canManageCategories ? (
					<Button label="Add Category" icon={"plus"} onClick={() => setShowInputBox(true)} />
				) : null}
			</div>

			<div className="mt-4">
				<div className="d-flex gap-4 justify-content-between align-items-center">
					<div className='text-nowrap'>
						<Heading level={4}>All Tags ({filteredTags.length})</Heading>
					</div>
					<InputText
						type='search'
						placeholder="Search tags..."
						value={searchTerm}
						onChange={setSearchTerm}
						size="small"
					/>
				</div>
				<div className="mt-3 mb-2 d-flex flex-wrap gap-2" style={currentlyEditing ? { overflowY: "scroll", maxHeight: "55vh" } : { overflow: "auto" }}>
					{filteredTags.map((tag) => (
						<div key={tag._id}>
							<Tag
								tag={tag}
								source={"All Tags"}
								moveTag={handleMoveTag}
								canManageCategories={canManageCategories}
								sameTagsClickHandler={sameTagsClickHandler}
								userTags={userTags}
							/>
						</div>
					))}
					{isLoading && (
						<div className="p-2">
							<Glimmer shape={"rectangle"} size={"large"} timing={"base"} />
							<div className="mt-3"></div>
							<Glimmer shape={"rectangle"} size={"base"} timing={"base"} />
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default TagCloudCategory;
